import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'
import { ExclamationCircleIcon } from '@heroicons/react/outline'
import { useState } from 'react'
import { NumericFormat } from 'react-number-format'

import { Portfolio, TaxBudgetDto, TradePreference } from 'types'

import { paveApi } from 'api'
import ModalLoader from 'components/common/Loader/ModalLoader'
import TaxBudgetTradingCadenceModal from 'components/partials/Portfolio/V2/Modals/TaxBudgetTradingCadenceModal'
import { TAX_BUDGET_NEARING_LIMIT } from 'constants/portfolio.constants'
import PDSBanner from 'designSystem/components/PDSBanner/PDSBanner'
import PDSBox from 'designSystem/components/PDSBox/PDSBox'
import PDSButton from 'designSystem/components/PDSButton/PDSButton'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSHeader from 'designSystem/components/PDSHeader/PDSHeader'
import PDSSeparator from 'designSystem/components/PDSSeparator/PDSSeparator'
import PDSText from 'designSystem/components/PDSText/PDSText'
import PDSTextInput from 'designSystem/components/PDSTextInput/PDSTextInput'
import PDSTooltip from 'designSystem/components/PDSTooltip/PDSTooltip'
import { COLOR } from 'designSystem/foundations/constants/color'
import { FONT_WEIGHT } from 'designSystem/foundations/constants/fontWeight'
import { SIZE } from 'designSystem/foundations/constants/size'
import { formatAsCurrency } from 'utils'
import { getPortfolioTaxBudgetPayload } from 'utils/portfolioFormUtil'
import { buildUpdatePreferencesRequest } from 'utils/portfolioUtil'

type Props = {
  isOpen: boolean
  onClose: () => void
  annualRealizedGainTotal: number
  taxBudget: TaxBudgetDto
  portfolio: Portfolio
}

function EditGainsModal({
  isOpen,
  onClose,
  annualRealizedGainTotal,
  taxBudget,
  portfolio,
}: Props) {
  const totalTaxBudget = taxBudget?.longTermTaxBudget || null
  const percentage = totalTaxBudget
    ? annualRealizedGainTotal / totalTaxBudget
    : 0
  const isNearingLimit = percentage >= TAX_BUDGET_NEARING_LIMIT

  const [newTarget, setNewTarget] = useState<string | null>(
    String(totalTaxBudget)
  )
  const [isTradingCadenceModalOpen, setIsTradingCadenceModalOpen] =
    useState(false)

  const [updateInvestmentPlanPreferences, { isLoading: isEditLoading }] =
    paveApi.useUpdateInvestmentPlanPreferencesMutation()

  const closeTradingCadenceModal = () => {
    setIsTradingCadenceModalOpen(false)
  }

  const handleSubmit = (tradePreference: TradePreference) => {
    closeTradingCadenceModal()

    const portfolioPayload = getPortfolioTaxBudgetPayload(
      portfolio,
      newTarget,
      tradePreference
    )

    const updatePreferencesRequest = buildUpdatePreferencesRequest(
      portfolio,
      portfolioPayload
    )

    updateInvestmentPlanPreferences(updatePreferencesRequest).unwrap()
  }

  return (
    <>
      <Modal
        isCentered
        isOpen={isOpen}
        onClose={() => {
          onClose()
        }}
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader />
          <ModalCloseButton />
          <ModalBody paddingX={0}>
            <PDSFlexBox
              flexDirection="column"
              gap={SIZE.s20}
              paddingX={SIZE.s32}
            >
              <PDSText
                fontWeight={FONT_WEIGHT.semibold}
                color={COLOR.textPrimary}
                size="lg"
              >
                Edit Gains Budget
              </PDSText>
              {isNearingLimit && (
                <PDSBanner variant="warning" fullBorder>
                  <PDSFlexBox gap={SIZE.s4} alignItems="center">
                    <ExclamationCircleIcon
                      height={16}
                      width={16}
                      color={COLOR.warning600}
                    />
                    <PDSText
                      size="sm"
                      color={COLOR.textPrimary}
                      fontWeight={FONT_WEIGHT.semibold}
                    >
                      Portfolio nearing annual gains limit.
                    </PDSText>
                  </PDSFlexBox>
                </PDSBanner>
              )}
              <PDSText
                color={COLOR.textSecondary}
                fontWeight={FONT_WEIGHT.medium}
                variant="p"
                size="md"
              >
                When this account reaches the gains limit, trading may be
                limited until there are losses to be realized.
              </PDSText>
              <PDSText
                color={COLOR.textTertiary}
                fontWeight={FONT_WEIGHT.semibold}
                variant="p"
                size="sm"
              >
                Current Utilization
              </PDSText>
              <PDSFlexBox gap={SIZE.s4}>
                <PDSHeader color={COLOR.textPrimary} variant="h4">
                  {formatAsCurrency(annualRealizedGainTotal)}
                </PDSHeader>
                <PDSHeader
                  color={COLOR.textButtonPrimary_disabled}
                  variant="h4"
                >
                  /
                </PDSHeader>
                <PDSHeader
                  color={COLOR.textButtonPrimary_disabled}
                  variant="h4"
                >
                  {formatAsCurrency(taxBudget?.longTermTaxBudget)}
                </PDSHeader>
              </PDSFlexBox>
              <PDSBox
                width="100%"
                height="8px"
                borderRadius="8px"
                backgroundColor={COLOR.content_active}
                overflow="hidden"
              >
                <PDSBox
                  width={percentage}
                  height="100%"
                  backgroundColor={
                    isNearingLimit ? COLOR.warning400 : COLOR.success400
                  }
                />
              </PDSBox>
              <PDSFlexBox flexDirection="column" gap={SIZE.s8}>
                <PDSFlexBox gap={SIZE.s8} alignItems="center">
                  <PDSText
                    color={COLOR.textSecondary}
                    fontWeight={FONT_WEIGHT.medium}
                    variant="p"
                    size="md"
                  >
                    Target Gains Budget
                  </PDSText>
                  <PDSTooltip
                    content={
                      <PDSFlexBox flexDirection="column" gap={SIZE.s4}>
                        <PDSText
                          color={COLOR.neutral200}
                          fontWeight={FONT_WEIGHT.semibold}
                        >
                          Target Annual Gains Budget
                        </PDSText>
                        <PDSText
                          color={COLOR.neutral200}
                          fontWeight={FONT_WEIGHT.medium}
                        >
                          The gains budget presented is a target estimate and
                          may vary based on the specific tax lots settled.
                        </PDSText>
                      </PDSFlexBox>
                    }
                  />
                </PDSFlexBox>

                <NumericFormat
                  allowLeadingZeros={false}
                  allowNegative={false}
                  customInput={PDSTextInput}
                  displayType="input"
                  onChange={(e) => setNewTarget(e.target.value)}
                  prefix="$"
                  thousandSeparator={true}
                  value={newTarget}
                />
              </PDSFlexBox>
            </PDSFlexBox>
          </ModalBody>
          <PDSSeparator marginTop={SIZE.s16} />
          <ModalFooter>
            <PDSFlexBox
              flexDirection="column"
              padding={SIZE.s32}
              gap={SIZE.s8}
              width="100%"
            >
              <PDSButton
                text="Confirm new budget"
                type="submit"
                disabled={newTarget === String(totalTaxBudget)}
                onClick={() => setIsTradingCadenceModalOpen(true)}
              />

              <PDSButton text="Cancel" variant="tertiary" onClick={onClose} />
            </PDSFlexBox>
          </ModalFooter>
          {isEditLoading && <ModalLoader />}
        </ModalContent>
      </Modal>
      <TaxBudgetTradingCadenceModal
        isOpen={isTradingCadenceModalOpen}
        onClose={closeTradingCadenceModal}
        handleSubmit={handleSubmit}
      />
    </>
  )
}
export default EditGainsModal
